import { Component, inject, ChangeDetectorRef, OnDestroy, OnInit } from '@angular/core';
import { DatePipe } from '@angular/common';
import { trigger, transition, style, animate } from '@angular/animations';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { UserService } from '../../../../services/user.service';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CounselorNav } from '../counselor-nav/counselor-nav';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatMenuModule } from '@angular/material/menu';
import { MatChipsModule } from '@angular/material/chips';
import { PostDialog } from '../../../common/post-dialog/post-dialog';
import { PostService } from '../../../../services/posts.service';
import { Post } from '../../../../models/post';
import { Comment } from '../../../../models/comment.js';
import { Subject, takeUntil, distinctUntilChanged, debounceTime, switchMap } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { UserLink } from '../../../common/user-link/user-link';
import { LinkedEvents } from '../../../common/linked-events/linked-events';

/** Available grade options for the posts filter. */
const GRADE_OPTIONS = [
  { label: 'Grade 6', value: 6 },
  { label: 'Grade 7', value: 7 },
  { label: 'Grade 8', value: 8 },
  { label: 'Grade 9', value: 9 },
  { label: 'Grade 10', value: 10 },
  { label: 'Grade 11', value: 11 },
  { label: 'Grade 12', value: 12 },
];

@Component({
  selector: 'app-counselor-posts',
  imports: [
    MatCardModule,
    MatIconModule,
    MatInputModule,
    MatButtonModule,
    MatFormFieldModule,
    MatSelectModule,
    CounselorNav,
    MatMenuModule,
    MatChipsModule,
    DatePipe,
    FormsModule,
    UserLink,
    LinkedEvents,
  ],
  templateUrl: './counselor-posts.html',
  styleUrl: './counselor-posts.css',
  standalone: true,
  animations: [
    trigger('fadeIn', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-16px)' }),
        animate('550ms ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
      ]),
    ]),
  ],
})
export class CounselorPosts implements OnInit, OnDestroy {
  constructor() {}
  /** Used for manual change detection in zones where async pipe isn't sufficient. */
  cdr = inject(ChangeDetectorRef);

  /** Service for opening Material dialogs. */
  dialog = inject(MatDialog);

  /** Service for post and comment CRUD operations. */
  postService = inject(PostService);

  /** Service for accessing the current authenticated user. */
  userService = inject(UserService);

  /** Shows a dismissible popup for comment/reply submit errors. */
  snackBar = inject(MatSnackBar);

  /** Secondary ChangeDetectorRef injected via inject() for use in callbacks. */
  cd = inject(ChangeDetectorRef);

  /** Currently displayed post list. */
  posts: Post[] = [];

  /** Current value of the search input field. */
  searchQuery = '';

  /** Debounced search input stream. */
  private searchSubject$ = new Subject<string>();

  /** Drives every post fetch, so a newer request cancels one still in flight. */
  private loadTrigger$ = new Subject<void>();

  /** Emits on destroy to unsubscribe all takeUntil streams. */
  private destroy$ = new Subject<void>();

  /** Tracks which post's comment section is currently expanded. Only one open at a time. */
  openCommentsPostId: number | null = null;

  /** Loaded comments keyed by post ID. Populated on demand when a post is expanded. */
  comments: { [postId: number]: Comment[] } = {};

  /** Tracks the draft comment text for each post's input field. */
  newComment: { [postId: number]: string } = {};

  /** Tracks the draft reply text for each comment's reply input. */
  replyText: { [commentId: number]: string } = {};

  /** The comment ID currently showing a reply input, or null if none. */
  activeReplyId: number | null = null;

  /** Comment counts keyed by post ID, used for display in the template. */
  commentCount: { [postId: number]: number } = {};

  /** Placeholder view count — replace with real analytics data when available. */
  views = 124;

  /** Whether the comment section is globally visible. */
  showComments = false;

  /** Available grade options sourced from profile-setup constants. */
  gradeOptions = GRADE_OPTIONS;

  /** Grade levels currently selected in the audience filter. */
  selectedGrades: number[] = [];

  /** Status filter chip options, mirroring the Career Pages Draft/Published pattern. */
  filters = ['All', 'Published', 'Draft'];

  /** Currently active status filter chip. */
  activeFilter = 'All';
  /** Page for pagination. */
  page = 0;
  /** Limit of posts grabbed at one time. */
  limit = 10;
  /** Are we loading more posts in. */
  isLoading = false;
  /** Are there any posts left to load. */
  hasMore = true;

  /**
   * Loads posts and sets up the debounced search subscription.
   * @returns {void}
   */
  ngOnInit(): void {
    this.loadTrigger$
      .pipe(
        switchMap(() => {
          this.isLoading = true;
          return this.postService.getPosts(
            this.limit,
            this.page * this.limit,
            this.searchQuery,
            this.selectedGrades,
            this.statusParam,
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((data) => {
        this.posts = [...this.posts, ...data];
        this.hasMore = data.length === this.limit;
        this.page++;
        this.isLoading = false;
        this.cdr.markForCheck();
      });

    this.searchSubject$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((query) => {
        this.searchQuery = query;
        this.loadPosts(true);
      });

    this.postService
      .onPostChanged()
      .pipe(takeUntil(this.destroy$))
      .subscribe((event) => {
        this.refreshPostsLive();

        if (this.openCommentsPostId === event.post_id) {
          this.loadComments(event.post_id);
        }
      });
    this.loadPosts(true);
  }

  /**
   * Returns the current authenticated user from UserService.
   * @returns {User | null}
   */
  get currentUser() {
    return this.userService.currentUser;
  }

  /**
   * Opens the PostDialog. The feed refreshes itself via the post:changed
   * socket event once the server confirms the create — reloading here too
   * raced that live refresh (both fetch independently, and whichever
   * resolves second appends onto the other's already-populated `posts`,
   * duplicating every post in view).
   * @returns {void}
   */
  openCreatePostDialog(): void {
    this.dialog.open(PostDialog, {
      width: '50vw',
      maxWidth: '100vw',
    });
  }

  /**
   * Removes a deleted post. The feed refreshes itself via the post:changed
   * socket event once the server confirms the delete — see
   * openCreatePostDialog's comment for why reloading here too caused
   * duplicate posts.
   * @returns {void}
   */
  ondeletePost(post: Post) {
    console.log('delete post');
    this.postService.deletePost(post.id, post).subscribe();
  }

  /**
   * Opens the PostDialog for editing. The feed refreshes itself via the
   * post:changed socket event once the server confirms the update — see
   * openCreatePostDialog's comment for why reloading here too caused
   * duplicate posts.
   * @returns {void}
   */
  openEditPostDialog(post: Post) {
    this.dialog.open(PostDialog, {
      width: '50vw',
      maxWidth: '100vw',
      data: post,
    });
  }

  /**
   * Toggles the comment section for a post. Collapses if already open;
   * otherwise opens it and fetches comments if not yet loaded.
   * @param {number} postId - The ID of the post to toggle comments for.
   * @returns {void}
   */
  toggleComments(postId: number): void {
    if (this.openCommentsPostId === postId) {
      this.openCommentsPostId = null;
    } else {
      this.openCommentsPostId = postId;
      this.loadComments(postId);
    }
  }

  /**
   * Toggles the reply input for a comment. Collapses if already active.
   * @param {number} commentId - The ID of the comment to toggle the reply input for.
   * @returns {void}
   */
  toggleReply(commentId: number): void {
    this.activeReplyId = this.activeReplyId === commentId ? null : commentId;
  }

  /**
   * Submits a new top-level comment on a post. Optimistically prepends
   * the new comment to the local list and increments the post's comment count.
   * @param {number} postId - The ID of the post to comment on.
   * @returns {void}
   */
  addComment(postId: number): void {
    const content = this.newComment?.[postId];
    if (!content || !content.trim()) return;

    this.postService.createComment(postId, content).subscribe({
      next: (newComment: any) => {
        this.comments[postId] ??= [];
        newComment.user = this.currentUser;
        newComment.replies = [];
        this.comments[postId].unshift(newComment);

        this.newComment[postId] = '';
        const post = this.posts.find((post) => post.id === postId);
        if (post) {
          post.commentCount += 1;
        }
        this.cdr.markForCheck();
      },
      error: (err) => {
        const message = err?.error?.error || 'Failed to post comment. Please try again.';
        this.snackBar.open(message, 'Dismiss', { duration: 5000 });
      },
    });
  }

  /**
   * Submits a reply to an existing comment. Appends the reply to the
   * parent comment's local replies array and increments the post's comment count.
   * @param {number} postId - The ID of the post the comment belongs to.
   * @param {number} parentId - The ID of the comment being replied to.
   * @returns {void}
   */
  addReply(postId: number, parentId: number): void {
    const content = this.replyText[parentId];
    if (!content?.trim()) return;

    this.postService.createComment(postId, content, parentId).subscribe({
      next: (newComment: any) => {
        const parentComment = this.comments[postId]?.find((c) => c.id === parentId);
        if (parentComment) {
          parentComment.replies ??= [];
          newComment.user = this.currentUser;
          parentComment.replies.push(newComment);
        }

        this.replyText[parentId] = '';
        const post = this.posts.find((post) => post.id === postId);
        if (post) {
          post.commentCount += 1;
        }
        this.activeReplyId = null;
        this.cdr.markForCheck();
      },
      error: (err) => {
        const message = err?.error?.error || 'Failed to post reply. Please try again.';
        this.snackBar.open(message, 'Dismiss', { duration: 5000 });
      },
    });
  }

  /**
   * Fetches comments for a post and stores them in the local comments map.
   * @param {number} postId - The ID of the post to load comments for.
   * @returns {void}
   */
  loadComments(postId: number): void {
    this.postService.getComments(postId).subscribe((data) => {
      this.comments[postId] = data;
      this.cdr.markForCheck();
    });
  }

  /**
   * Requests a page of posts using the current search query and grade filter.
   * @param {boolean} reset - Start over from the first page.
   * @returns {void}
   */
  loadPosts(reset = false): void {
    if (reset) {
      this.page = 0;
      this.posts = [];
      this.hasMore = true;
      this.isLoading = false;
    }

    if (this.isLoading || !this.hasMore) return;

    this.loadTrigger$.next();
  }
  loadMore(): void {
    this.loadPosts();
  }

  /**
   * Refetches the currently-loaded window of posts and replaces the list in
   * place, instead of loadPosts(true)'s hard reset (which clears the array
   * before refetching, causing the whole feed to flash empty). Used for
   * live-update refreshes. Sends the active search and grade filters so the
   * refreshed window stays scoped to what the counselor is looking at.
   * @returns {void}
   */
  private refreshPostsLive(): void {
    const count = Math.max(this.posts.length, this.limit);

    this.postService
      .getPosts(count, 0, this.searchQuery, this.selectedGrades, this.statusParam)
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        this.posts = data;
        this.hasMore = data.length === count;
        this.page = Math.ceil(data.length / this.limit);
        this.cdr.markForCheck();
      });
  }

  // ─── Search ───────────────────────────────────────────────────────────────

  /**
   * Pushes the query into the debounced search stream.
   * @param {string} query - The current search input value.
   * @returns {void}
   */
  onSearch(query: string): void {
    this.searchSubject$.next(query);
  }

  /**
   * Clears the search input and reloads the unfiltered list.
   * Goes through the debounced stream so `distinctUntilChanged` stays in sync.
   * @returns {void}
   */
  clearSearch(): void {
    this.searchQuery = '';
    this.searchSubject$.next('');
    this.cdr.markForCheck();
  }

  /**
   * Reloads posts when the grade filter selection changes.
   * @returns {void}
   */
  onGradeFilterChange(): void {
    this.loadPosts(true);
  }

  /**
   * Maps the active status filter chip to the server's `status` query value.
   * Posts are server-paginated (unlike the Career Pages fetch-all-then-filter
   * list), so this has to drive a real refetch rather than filtering whatever
   * page happens to already be loaded.
   * @returns {string}
   */
  private get statusParam(): string {
    if (this.activeFilter === 'Published') return 'published';
    if (this.activeFilter === 'Draft') return 'draft';
    return '';
  }

  /**
   * Reloads posts from the server when the status filter chip changes.
   * @returns {void}
   */
  onStatusFilterChange(): void {
    this.loadPosts(true);
  }

  /**
   * Publishes a draft post in place. The feed refreshes itself via the
   * post:changed socket event once the server confirms the update — see
   * openCreatePostDialog's comment for why reloading here too caused
   * duplicate posts.
   * @param {Post} post - The draft post to publish.
   * @returns {void}
   */
  publishPost(post: Post): void {
    this.postService.updatePost(post.id, { content: post.content, status: 'edited' }).subscribe({
      error: (err) => {
        const message = err?.error?.error || 'Failed to publish post. Please try again.';
        this.snackBar.open(message, 'Dismiss', { duration: 5000 });
      },
    });
  }

  // formats grades a b c -> a-c OR a b c z -> a-c, z
  formatGradeRanges(grades: number[]): string[] {
    if (!grades?.length) return [];
    const sorted = [...new Set(grades)].sort((a, b) => a - b);
    const ranges: string[] = [];
    let start = sorted[0];
    let prev = sorted[0];

    const ordinal = (n: number) => `${n}th`;
    const pushRange = () => {
      ranges.push(start === prev ? ordinal(start) : `${ordinal(start)}–${ordinal(prev)}`);
    };

    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i] === prev + 1) {
        prev = sorted[i];
      } else {
        pushRange();
        start = prev = sorted[i];
      }
    }
    pushRange();
    return ranges;
  }

  // if we have over 3 badges pick the first 3 and add a +x remaining
  truncateBadges(items: string[], max = 3): { visible: string[]; overflow: number } {
    if (!items?.length) return { visible: [], overflow: 0 };
    return {
      visible: items.slice(0, max),
      overflow: Math.max(0, items.length - max),
    };
  }

  /**
   * Completes the destroy subject to clean up all active subscriptions.
   * @returns {void}
   */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
